import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

const SCRAPING_HOUR = 23;
const SCRAPING_MINUTE = 30;

const getNextScrapingDate = (): Date => {
  const now = new Date();
  const nextScraping = new Date();
  
  nextScraping.setHours(SCRAPING_HOUR, SCRAPING_MINUTE, 0, 0);
  
  if (now >= nextScraping) {
    nextScraping.setDate(nextScraping.getDate() + 1);
  }
  
  return nextScraping;
};

const formatTimeUntil = (nextDate: Date, language: string): string => {
  const now = new Date();
  const diff = nextDate.getTime() - now.getTime();
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.getDate() === today.getDate() && 
         date.getMonth() === today.getMonth() && 
         date.getFullYear() === today.getFullYear();
};

interface NextScrapingProgressProps {
  variant?: 'default' | 'compact';
}

export const NextScrapingProgress = ({ variant = 'default' }: NextScrapingProgressProps) => {
  const { t, language } = useLanguage();
  const [nextScraping, setNextScraping] = useState(getNextScrapingDate());
  const [timeUntil, setTimeUntil] = useState(formatTimeUntil(nextScraping, language));

  useEffect(() => {
    const interval = setInterval(() => {
      const nextDate = getNextScrapingDate();
      setNextScraping(nextDate);
      setTimeUntil(formatTimeUntil(nextDate, language));
    }, 60000);

    return () => clearInterval(interval);
  }, [language]);

  const todayLabel = isToday(nextScraping) 
    ? (language === 'pl' ? 'dziś' : 'today')
    : nextScraping.toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });

  const timeLabel = `${String(SCRAPING_HOUR).padStart(2, '0')}:${String(SCRAPING_MINUTE).padStart(2, '0')}`;

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <RefreshCw className="h-3 w-3" />
        <span>{language === 'pl' ? 'Następna aktualizacja' : 'Next update'}: {todayLabel}, {timeLabel}</span>
        <Badge variant="outline" className="gap-1 text-xs py-0 px-1.5">
          <Clock className="h-2.5 w-2.5" />
          {timeUntil}
        </Badge>
      </div>
    );
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm text-foreground">
                {language === 'pl' ? 'Następna aktualizacja danych' : 'Next data update'}
              </h3>
              <p className="text-xs text-muted-foreground truncate">
                {todayLabel}, {timeLabel}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1.5 flex-shrink-0">
            <Clock className="h-3 w-3" />
            {timeUntil}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};
